import { useEffect, useMemo, useReducer, useRef, useCallback } from "react";
import moment from "moment";

const initialLocal = {
  startDateAlarm: null,
  endDateAlarm: null,
  tempAlarm: 0,
  impactAlarm: 0,
  openAlarm: 0,
  humAlarm: 0,
  onDateRangePicked: false,
  onParameterPicked: false,
  searchWasMade: false,
  triggerSearch: false,
  page: 1,
  size: 10,
};

function reducer(state, action) {
  switch (action.type) {
    case "hidrateSaved": {
      const { startISO, endISO, flags, page, size } = action.payload;
      return {
        ...state,
        startDateAlarm: startISO ? moment(startISO) : null,
        endDateAlarm: endISO ? moment(endISO) : null,
        onDateRangePicked: !!(startISO && endISO),
        ...flags,
        page: page ?? state.page,
        size: size ?? state.size,
      };
    }
    case "setPageSize":
      return { ...state, page: action.page, size: action.size };
    case "setFlags":
      return {
        ...state,
        ...action.flags,
        onParameterPicked: true,
        triggerSearch: false,
      };
    case "setDates": {
      const [start, end] = action.dates || [];
      return {
        ...state,
        startDateAlarm: start || null,
        endDateAlarm: end || null,
        onDateRangePicked: !!(start && end),
        triggerSearch: false,
      };
    }
    case "armedSearch":
      return {
        ...state,
        ...action.flagsForApi,
        searchWasMade: true,
        onParameterPicked: true,
        onDateRangePicked: true,
        triggerSearch: true,
        page: action.page ?? state.page,
        size: action.size ?? state.size,
      };
    case "consumeTrigger":
      return { ...state, triggerSearch: false };
    default:
      return state;
  }
}

export default function useAlarmsSearchController({
  alarmsCriteria,
  dispatchRedux,
  setAlarmsDateRange,
  setAlarmsSelectedLabels,
  setAlarmsCriteria,
  setAlarmsPagination,
  fetchAlarms,
  filterOptions,
}) {
  const [state, dispatch] = useReducer(reducer, initialLocal);

  const labelsToFlags = useCallback(
    (labels) => {
      const all = {
        tempAlarm: 0,
        impactAlarm: 0,
        openAlarm: 0,
        humAlarm: 0,
      };
      labels.forEach((lab) => {
        const f = filterOptions.find((o) => o.label === lab);
        if (f) all[f.value] = 1;
      });
      return all;
    },
    [filterOptions]
  );

  const allFlagsOn = useMemo(
    () => ({
      tempAlarm: 1,
      impactAlarm: 1,
      openAlarm: 1,
      humAlarm: 1,
    }),
    []
  );

  // Hidratar una sola vez con “saved” inicial y relanzar si procede
  const didAutoRun = useRef(false);
  useEffect(() => {
    // 1) Hidratar fechas+etiquetas (solo etiquetas explícitas)
    const flagsFromSaved = labelsToFlags(alarmsCriteria.selectedLabels || []);
    dispatch({
      type: "hidrateSaved",
      payload: {
        startISO: alarmsCriteria.startISO,
        endISO: alarmsCriteria.endISO,
        flags: flagsFromSaved,
        page: alarmsCriteria.page,
        size: alarmsCriteria.size,
      },
    });

    // 2) Autorelanzo SOLO al montar si ya había fechas guardadas
    if (
      !didAutoRun.current &&
      alarmsCriteria.startISO &&
      alarmsCriteria.endISO
    ) {
      didAutoRun.current = true;
      const noLabels =
        !alarmsCriteria.selectedLabels ||
        alarmsCriteria.selectedLabels.length === 0;
      const flagsForApi = noLabels ? allFlagsOn : flagsFromSaved;
      const initPage = alarmsCriteria.page ?? state.page;
      const initSize = alarmsCriteria.size ?? state.size;
      dispatch({
        type: "armedSearch",
        flagsForApi,
        page: initPage,
        size: initSize,
      });
    }
  }, []);

  // Un único efecto observador: si triggerSearch es true → se llama al back
  useEffect(() => {
    if (!state.triggerSearch) return;
    fetchAlarms(
      state.startDateAlarm,
      state.endDateAlarm,
      state.tempAlarm,
      state.impactAlarm,
      state.openAlarm,
      state.humAlarm,
      state.size,
      state.page,
      false
    );
    dispatch({ type: "consumeTrigger" });
  }, [
    state.triggerSearch,
    state.startDateAlarm,
    state.endDateAlarm,
    state.tempAlarm,
    state.impactAlarm,
    state.openAlarm,
    state.humAlarm,
    state.size,
    state.page,
    fetchAlarms,
  ]);

  // Handlers públicos
  const onDateChange = useCallback(
    (dates) => {
      dispatch({ type: "setDates", dates });
      dispatchRedux(
        setAlarmsDateRange({
          startISO: dates?.[0] ? dates[0].toISOString() : null,
          endISO: dates?.[1] ? dates[1].toISOString() : null,
        })
      );
    },
    [dispatchRedux, setAlarmsDateRange]
  );

  const onExtraFilterChange = useCallback(
    (labels) => {
      const flags = labelsToFlags(labels);
      dispatch({ type: "setFlags", flags });
      dispatchRedux(setAlarmsSelectedLabels(labels));
    },
    [dispatchRedux, setAlarmsSelectedLabels, labelsToFlags]
  );

  const onSearch = useCallback(() => {
    const {
      startDateAlarm,
      endDateAlarm,
      tempAlarm,
      impactAlarm,
      openAlarm,
      humAlarm,
      size,
    } = state;
    if (!startDateAlarm || !endDateAlarm) return;

    const alarmFlags = {
      tempAlarm,
      impactAlarm,
      openAlarm,
      humAlarm,
    };
    const noneSelected = Object.values(alarmFlags).every((v) => v === 0);
    const flagsForApi = noneSelected ? allFlagsOn : alarmFlags;

    // Persistimos snapshot de criterios visibles (fechas + etiquetas elegidas)
    dispatchRedux(
      setAlarmsCriteria({
        startISO: startDateAlarm.toISOString(),
        endISO: endDateAlarm.toISOString(),
        selectedLabels: alarmsCriteria.selectedLabels || [],
      })
    );
    dispatchRedux(setAlarmsPagination({ page: 1, size }));

    dispatch({ type: "armedSearch", flagsForApi, page: 1, size });
  }, [
    state,
    allFlagsOn,
    dispatchRedux,
    setAlarmsCriteria,
    setAlarmsPagination,
    alarmsCriteria.selectedLabels,
  ]);

  const onPaginateChange = useCallback(
    (page, size) => {
      dispatchRedux(setAlarmsPagination({ page, size }));
      dispatch({ type: "setPageSize", page, size });

      fetchAlarms(
        state.startDateAlarm,
        state.endDateAlarm,
        state.tempAlarm,
        state.impactAlarm,
        state.openAlarm,
        state.humAlarm,
        size,
        page,
        true
      );
    },
    [dispatchRedux, setAlarmsPagination, fetchAlarms, state]
  );

  return {
    searchParameters: state,
    onDateChange,
    onExtraFilterChange,
    onSearch,
    onPaginateChange,
  };
}
